// @ts-check
// Editor da página de agendamento (contrato 10 de agendamento, seção 14): compartilhar (link, iframe e API para o site),
// evento, disponibilidade (agenda do Google ou horário semanal, anfitriões, dias bloqueados, antecedência e intervalo),
// formulário, o que acontece depois de marcar, situação, prévia dos horários, reservas e excluir.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import {
  BOOKING_EVENT_TYPES, BOOKING_LIMITS, BOOKING_QUESTION_KINDS, SINGLE_USE_QUESTION_KINDS, defaultDurationFor,
} from '../../../../shared/booking/booking-values.js';
import {
  cancelPageBooking, deleteBookingPage, getBookingPage, listBookingHosts, listPageBookings, updateBookingPage,
} from '../../data/booking-pages.js';
import { isMockEnabled } from '../../data/api-client.js';
import { currentWorkspaceMemberState, hasCurrentPermissionFlag, objectsByNameSingular } from '../../data/metadata-store.js';
import { blockedDatesText, draftFromPage, parseBlockedDatesText, patchFromDraft, siteSnippets } from '../../lib/booking-pages.js';
import { formatDateTime } from '../../lib/format.js';
import { Link, navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { CopyableValue } from '../settings/settings-channels-page.js';
import { AdminOnlyNote, CodeSnippet, FormError, SelectField, StatusBadge, TextField, describeError } from '../settings/integration-ui.js';
import { ApiError } from '../../data/api-client.js';
import { Button, IconButton } from '../../ui/button.js';
import { Checkbox, Skeleton, Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { BookingHostPicker } from './booking-host-picker.js';
import { runHostAction } from './host-resolution.js';
import { BookingSlotPreview } from './booking-slot-preview.js';
import { WeeklyHoursEditor } from './weekly-hours-editor.js';

/**
 * @typedef {import('../../data/booking-pages.js').BookingPage} BookingPage
 * @typedef {import('../../data/booking-pages.js').BookingHost} BookingHost
 * @typedef {import('../../data/booking-pages.js').BookingRow} BookingRow
 * @typedef {import('../../../../shared/booking/booking-values.js').BookingPageValues} BookingPageValues
 * @typedef {import('../../../../shared/booking/booking-values.js').BookingQuestion} BookingQuestion
 */

const TIME_ZONES = Object.freeze([
  'America/Sao_Paulo', 'America/Bahia', 'America/Fortaleza', 'America/Recife', 'America/Belem', 'America/Manaus', 'America/Cuiaba',
  'America/Campo_Grande', 'America/Porto_Velho', 'America/Boa_Vista', 'America/Rio_Branco', 'America/Noronha', 'Europe/Lisbon',
]);
const BOOKING_SCOPES = Object.freeze(/** @type {const} */ (['UPCOMING', 'PAST', 'CANCELED']));

/**
 * @param {{ label: string, value: number, onChange: (value: number) => void, min: number, max: number, hint?: string, disabled?: boolean }} props
 */
function NumberField({ label, value, onChange, min, max, hint, disabled = false }) {
  return html`<label class="form-field">
    <span class="form-field-label">${label}</span>
    <input class="text-input" type="number" inputmode="numeric" min=${min} max=${max} value=${value} disabled=${disabled}
      onInput=${(/** @type {Event} */ event) => {
        const next = Number(/** @type {HTMLInputElement} */ (event.currentTarget).value);
        if (Number.isFinite(next)) onChange(Math.round(next));
      }} />
    ${hint ? html`<span class="input-hint">${hint}</span>` : null}
  </label>`;
}

/** @param {string} objectName @param {string} fieldName */
function optionsOf(objectName, fieldName) {
  const field = objectsByNameSingular.value.get(objectName)?.fields.find((candidate) => candidate.name === fieldName);
  return (field?.options ?? []).map((option) => ({ value: option.value, label: translateMetadataLabel(option.label) }));
}

/**
 * Perguntas configuráveis do formulário (nome, e-mail e WhatsApp são fixos).
 * @param {{ questions: BookingQuestion[], onChange: (questions: BookingQuestion[]) => void, disabled: boolean }} props
 */
function QuestionsEditor({ questions, onChange, disabled }) {
  /** @param {number} index @param {Partial<BookingQuestion>} patch */
  const update = (index, patch) => onChange(questions.map((question, position) => (position === index ? { ...question, ...patch } : question)));
  const usedSingleKinds = new Set(questions.map((question) => question.kind).filter((kind) => SINGLE_USE_QUESTION_KINDS.includes(kind)));
  const add = () => {
    const ids = new Set(questions.map((question) => question.id));
    let next = questions.length + 1;
    while (ids.has(`q${next}`)) next += 1;
    onChange([...questions, { id: `q${next}`, kind: 'TEXT', label: '', isRequired: false }]);
  };
  return html`<div class="booking-questions">
    <p class="input-hint">${t('bookingPages.questions.fixed')}</p>
    ${questions.map((question, index) => html`<div key=${question.id} class="booking-question">
      <div class="form-field-row">
        <label class="form-field">
          <span class="form-field-label">${t('bookingPages.questions.kind')}</span>
          <select class="text-input" value=${question.kind} disabled=${disabled}
            onChange=${(/** @type {Event} */ event) => {
              const kind = /** @type {BookingQuestion['kind']} */ (/** @type {HTMLSelectElement} */ (event.currentTarget).value);
              update(index, { kind, label: question.label || t(`bookingPages.questions.defaultLabels.${kind}`), options: kind === 'SELECT' ? question.options ?? ['', ''] : undefined });
            }}>
            ${BOOKING_QUESTION_KINDS.filter((kind) => kind === question.kind || !SINGLE_USE_QUESTION_KINDS.includes(kind) || !usedSingleKinds.has(kind))
              .map((kind) => html`<option key=${kind} value=${kind}>${t(`bookingPages.questions.kinds.${kind}`)}</option>`)}
          </select>
        </label>
        <${TextField} label=${t('bookingPages.questions.label')} value=${question.label} maxLength=${BOOKING_LIMITS.questionLabelLength} onInput=${(value) => update(index, { label: value })} />
      </div>
      ${question.kind === 'SELECT'
        ? html`<${TextField} label=${t('bookingPages.questions.options')} hint=${t('bookingPages.questions.optionsHint')} multiline=${true} value=${(question.options ?? []).join('\n')}
            onInput=${(value) => update(index, { options: value.split('\n').map((option) => option.trimStart()) })} />`
        : null}
      <div class="booking-question-footer">
        <label class="booking-inline-check">
          <${Checkbox} checked=${question.isRequired} disabled=${disabled} ariaLabel=${t('bookingPages.questions.required')}
            onChange=${(/** @type {boolean} */ checked) => update(index, { isRequired: checked })} />
          <span>${t('bookingPages.questions.required')}</span>
        </label>
        <${IconButton} icon="IconTrash" variant="tertiary" size="small" disabled=${disabled} ariaLabel=${t('bookingPages.questions.remove')}
          onClick=${() => onChange(questions.filter((_question, position) => position !== index))} />
      </div>
    </div>`)}
    ${questions.length < BOOKING_LIMITS.questions
      ? html`<div><${Button} title=${t('bookingPages.questions.add')} icon="IconPlus" variant="secondary" size="small" disabled=${disabled} onClick=${add} /></div>`
      : null}
  </div>`;
}

/**
 * Reservas da página com Cancelar.
 * @param {{ bookingPageId: string, canEdit: boolean, refreshKey: string }} props
 */
function BookingList({ bookingPageId, canEdit, refreshKey }) {
  const [scope, setScope] = useState(/** @type {typeof BOOKING_SCOPES[number]} */ ('UPCOMING'));
  const [bookings, setBookings] = useState(/** @type {BookingRow[] | null} */ (null));
  const [canceling, setCanceling] = useState(/** @type {BookingRow | null} */ (null));
  const [isCanceling, setIsCanceling] = useState(false);
  const load = () => {
    setBookings(null);
    listPageBookings(bookingPageId, scope).then(setBookings).catch((error) => {
      showErrorSnackbar(error);
      setBookings([]);
    });
  };
  useEffect(load, [bookingPageId, scope, refreshKey]);
  /** @param {BookingRow} booking */
  const deliveryLabel = (booking) => (booking.providerStatus === 'SYNCED'
    ? t(booking.provider === 'GOOGLE' ? 'bookingPages.bookings.delivery.GOOGLE' : 'bookingPages.bookings.delivery.ICS')
    : t(`bookingPages.bookings.delivery.${booking.providerStatus}`));
  return html`<div class="booking-list">
    <div class="booking-segmented" role="tablist" aria-label=${t('bookingPages.bookings.title')}>
      ${BOOKING_SCOPES.map((item) => html`<button key=${item} type="button" role="tab" aria-selected=${scope === item ? 'true' : 'false'}
        class="booking-segment" data-selected=${scope === item ? 'true' : 'false'} onClick=${() => setScope(item)}>${t(`bookingPages.bookings.scopes.${item}`)}</button>`)}
    </div>
    ${bookings === null
      ? html`<${Skeleton} height=${60} radius=${8} />`
      : bookings.length === 0
        ? html`<div class="integration-empty">${t(`bookingPages.bookings.empty.${scope}`)}</div>`
        : html`<div class="settings-table integration-table">
            <div class="settings-table-row settings-table-header" data-columns="bookings">
              <span>${t('bookingPages.bookings.when')}</span><span>${t('bookingPages.bookings.who')}</span><span>${t('bookingPages.bookings.host')}</span>
              <span>${t('bookingPages.bookings.invitation')}</span><span></span>
            </div>
            ${bookings.map((booking) => html`<div key=${booking.id} class="settings-table-row" data-columns="bookings">
              <span class="settings-table-name">${formatDateTime(booking.startsAt)}</span>
              <span class="booking-list-who">
                ${booking.enrollmentOpportunityId
                  ? html`<${Link} href=${`/object/enrollmentOpportunity/${booking.enrollmentOpportunityId}`} className="integration-link">${booking.inviteeName || t('bookingPages.bookings.openCard')}<//>`
                  : html`<span>${booking.inviteeName || t('bookingPages.bookings.anonymized')}</span>`}
                ${booking.inviteeEmail || booking.inviteePhone ? html`<span class="settings-table-muted">${[booking.inviteeEmail, booking.inviteePhone].filter(Boolean).join(' · ')}</span>` : null}
                ${booking.source === 'BOT' ? html`<span class="settings-table-muted">${t('bookingPages.bookings.fromBot')}</span>` : null}
              </span>
              <span class="settings-table-muted">${booking.hostName ?? ''}</span>
              <span>${booking.status === 'CANCELED'
                ? html`<${StatusBadge} status="CANCELLED" label=${t(`bookingPages.bookings.canceledBy.${booking.cancelReason ?? 'TEAM'}`)} />`
                : html`<${StatusBadge} status=${booking.providerStatus === 'SYNCED' ? 'ACTIVE' : booking.providerStatus === 'FAILED' ? 'FAILED' : 'PENDING'} label=${deliveryLabel(booking)} />`}</span>
              <span>${canEdit && booking.status === 'CONFIRMED' && scope === 'UPCOMING'
                ? html`<${IconButton} icon="IconX" variant="tertiary" size="small" ariaLabel=${t('bookingPages.bookings.cancel')} onClick=${() => setCanceling(booking)} />`
                : null}</span>
            </div>`)}
          </div>`}
    ${canceling ? html`<${Modal} title=${t('bookingPages.bookings.cancelTitle')} subtitle=${t('bookingPages.bookings.cancelSubtitle', { when: formatDateTime(canceling.startsAt) })}
      confirmLabel=${t('bookingPages.bookings.cancel')} confirmAccent="danger" isLoading=${isCanceling} onClose=${() => setCanceling(null)}
      onConfirm=${async () => {
        setIsCanceling(true);
        try {
          await cancelPageBooking(bookingPageId, canceling.id);
          showSuccessSnackbar(t('bookingPages.bookings.canceledSnackbar'));
          setCanceling(null);
          load();
        } catch (error) {
          showErrorSnackbar(error);
        } finally {
          setIsCanceling(false);
        }
      }} />` : null}
  </div>`;
}

/**
 * @param {{ bookingPageId: string }} props
 */
export function SettingsBookingPageDetailPage({ bookingPageId }) {
  const [page, setPage] = useState(/** @type {BookingPage | null} */ (null));
  const [draft, setDraft] = useState(/** @type {BookingPageValues | null} */ (null));
  const [hosts, setHosts] = useState(/** @type {BookingHost[] | null} */ (null));
  const [blockedText, setBlockedText] = useState('');
  const [originsText, setOriginsText] = useState('');
  const [guestsText, setGuestsText] = useState('');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [invalidMemberIds, setInvalidMemberIds] = useState(/** @type {string[]} */ ([]));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [busyMemberId, setBusyMemberId] = useState(/** @type {string | null} */ (null));
  const canEdit = hasCurrentPermissionFlag('WORKSPACE');
  const currentMemberId = currentWorkspaceMemberState.value?.id ?? null;
  const connectedParam = searchParamsState.value.get('connected');
  const hostParam = searchParamsState.value.get('host');
  const connectionError = searchParamsState.value.get('error');

  /** @param {BookingPage} loaded */
  const apply = (loaded) => {
    setPage(loaded);
    const next = draftFromPage(loaded);
    setDraft(next);
    setBlockedText(blockedDatesText(next.blockedDates));
    setOriginsText(next.allowedOrigins.join('\n'));
    setGuestsText(next.fixedGuestEmails.join('\n'));
  };
  useEffect(() => {
    getBookingPage(bookingPageId).then((loaded) => {
      apply(loaded);
      document.title = `${loaded.name} - ${t('bookingPages.title')}`;
    }).catch((requestError) => {
      showErrorSnackbar(requestError);
      navigate('/settings/booking-pages', { replace: true });
    });
    listBookingHosts().then(setHosts).catch(() => setHosts([]));
  }, [bookingPageId]);
  // volta do Google depois do aceite em um clique (3.5.0): a lista de anfitriões já traz a permissão nova
  useEffect(() => {
    if (!connectedParam && !connectionError) return;
    if (connectedParam) showSuccessSnackbar(t(hostParam ? 'bookingPages.hostActions.readySnackbar' : 'calendar.accounts.connectedSnackbar', { provider: t('calendar.accounts.providers.GOOGLE') }));
    if (connectionError) showErrorSnackbar(new Error(t(`calendar.accounts.errors.${connectionError === 'ACCESS_DENIED' ? 'ACCESS_DENIED' : 'OAUTH_FAILED'}`)));
    updateSearchParams({ connected: null, host: null, error: null, provider: null });
    listBookingHosts().then(setHosts).catch(() => {});
  }, [connectedParam, connectionError]);

  const breadcrumb = html`<nav class="record-breadcrumb">
    <${Link} href="/settings/booking-pages" className="record-breadcrumb-prefix">${t('bookingPages.title')}<//>
    <span class="record-breadcrumb-separator">/</span>
    <span class="record-breadcrumb-current">${page?.name ?? ''}</span>
  </nav>`;
  if (!page || !draft) return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} />`}><div class="settings-page"><${Skeleton} height=${240} radius=${8} /></div><//>`;

  /** @param {Partial<BookingPageValues>} patch */
  const change = (patch) => setDraft({ ...draft, ...patch });
  const lines = (/** @type {string} */ text) => text.split(/[\n,;]+/).map((item) => item.trim()).filter(Boolean);
  const blocked = parseBlockedDatesText(blockedText);
  const snippets = siteSnippets(page);

  /**
   * Grava o que mudou; devolve true quando ficou salvo. `silent` não mostra o aviso de salvo (gravação antes de ir ao Google).
   * @param {Partial<BookingPageValues> & { regeneratePublicId?: boolean }} [extra] @param {{ silent?: boolean }} [options]
   */
  const save = async (extra = {}, options = {}) => {
    if (blocked.invalid.length > 0) {
      setError(t('bookingPages.editor.blockedDatesInvalid', { dates: blocked.invalid.join(', ') }));
      return false;
    }
    setIsSaving(true);
    setError(null);
    setInvalidMemberIds([]);
    const next = { ...draft, blockedDates: blocked.dates, allowedOrigins: lines(originsText), fixedGuestEmails: lines(guestsText), ...extra };
    try {
      const patch = { ...patchFromDraft(page, next), ...(extra.regeneratePublicId ? { regeneratePublicId: true } : {}) };
      const saved = Object.keys(patch).length > 0 ? await updateBookingPage(page.id, patch) : page;
      apply(saved);
      if (!options.silent) showSuccessSnackbar(t('bookingPages.editor.saved'));
      return true;
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.code === 'BOOKING_HOST_ACCOUNT_REQUIRED') {
        setInvalidMemberIds(/** @type {string[]} */ (/** @type {any} */ (requestError.details)?.workspaceMemberIds ?? []));
      }
      const reason = requestError instanceof ApiError ? /** @type {any} */ (requestError.details)?.reason : null;
      // motivo conhecido da regra da página: o texto da escola no lugar do código
      setError(reason ? t(`bookingPages.reasons.${reason}`) : describeError(requestError));
      return false;
    } finally {
      setIsSaving(false);
      setIsRegenerating(false);
    }
  };

  /**
   * Resolve a pendência de um anfitrião (3.5.0): convite para outro membro; para a própria pessoa, o aceite pela conta
   * (e a ida ao Google pela permissão de criar eventos, voltando a esta página). A página é gravada antes de sair, para a
   * escolha do anfitrião não se perder.
   * @param {BookingHost} host @param {'ACCEPT' | 'CONNECT' | 'INVITE'} action
   */
  const resolveHost = async (host, action) => {
    setBusyMemberId(host.workspaceMemberId);
    try {
      if (action !== 'INVITE' && !(await save({}, { silent: true }))) return;
      const chosenAccountId = draft.hosts.find((item) => item.workspaceMemberId === host.workspaceMemberId)?.connectedAccountId ?? null;
      const result = await runHostAction(host, action, { bookingPageId: page.id, returnTo: 'BOOKING_PAGE', returnToId: page.id, chosenAccountId });
      if (result.outcome === 'INVITED' || result.outcome === 'ALREADY_INVITED') {
        showSuccessSnackbar(t(result.outcome === 'INVITED' ? 'bookingPages.hostActions.invited' : 'bookingPages.hostActions.alreadyInvited', { name: result.memberName }));
      } else if (result.outcome === 'READY') {
        setHosts(await listBookingHosts());
        showSuccessSnackbar(t('bookingPages.hostActions.readySnackbar'));
      }
    } catch (requestError) {
      showErrorSnackbar(requestError);
    } finally {
      setBusyMemberId(null);
    }
  };
  const stageOptions = optionsOf('enrollmentOpportunity', 'stage').filter((option) => !['ENROLLED', 'LOST', 'NOT_A_LEAD'].includes(option.value));
  const leadSourceOptions = optionsOf('person', 'leadSource');

  return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} actions=${html`<div class="booking-header-actions">
      ${isMockEnabled() ? null : html`<${Button} title=${t('bookingPages.editor.open')} icon="IconExternalLink" variant="secondary" size="small" onClick=${() => window.open(page.publicUrl, '_blank', 'noopener')} />`}
      ${canEdit ? html`<${Button} title=${t('bookingPages.editor.save')} icon="IconCheck" accent="blue" size="small" isLoading=${isSaving} disabled=${!draft.name.trim()} onClick=${() => save()} />` : null}
    </div>`} />`}>
    <div class="settings-page booking-editor" data-testid="booking-page-editor">
      ${!canEdit ? html`<${AdminOnlyNote} />` : null}
      <div class="booking-status-card" data-active=${page.isActive ? 'true' : 'false'}>
        <div>
          <${StatusBadge} status=${page.isActive ? 'ACTIVE' : 'PAUSED'} label=${page.isActive ? t('bookingPages.status.active') : t('bookingPages.status.paused')} />
          <p class="input-hint">${page.isActive ? t('bookingPages.status.activeHint') : t('bookingPages.status.pausedHint')}</p>
        </div>
        ${canEdit ? html`<${Button} title=${page.isActive ? t('bookingPages.status.pause') : t('bookingPages.status.activate')}
          icon=${page.isActive ? 'IconPlayerPause' : 'IconCheck'} accent=${page.isActive ? 'default' : 'blue'} variant=${page.isActive ? 'secondary' : 'primary'} size="small"
          isLoading=${isSaving} onClick=${() => save({ isActive: !page.isActive })} />` : null}
      </div>
      <${FormError} message=${error} />
      <fieldset class="integration-fieldset" disabled=${!canEdit}>
        <${SettingsSection} title=${t('bookingPages.editor.shareTitle')} description=${t('bookingPages.editor.shareDescription')}>
          <div class="form-field"><span class="form-field-label">${t('bookingPages.editor.publicUrl')}</span><${CopyableValue} value=${snippets.link} label=${t('bookingPages.editor.copyLink')} /></div>
          <${CodeSnippet} label=${t('bookingPages.editor.iframe')} code=${snippets.iframe} />
          <${CodeSnippet} label=${t('bookingPages.editor.api')} code=${snippets.api} />
          <${TextField} label=${t('bookingPages.editor.allowedOrigins')} hint=${t('bookingPages.editor.allowedOriginsHint')} multiline=${true} value=${originsText}
            onInput=${setOriginsText} placeholder="https://www.escola.com.br" />
          ${canEdit ? html`<div><${Button} title=${t('bookingPages.editor.regenerate')} icon="IconRefresh" variant="secondary" size="small" onClick=${() => setIsRegenerating(true)} /></div>` : null}
        <//>
        <${SettingsSection} title=${t('bookingPages.editor.eventTitle')} description=${t('bookingPages.editor.eventDescription')}>
          <div class="channel-form">
            <div class="form-field-row">
              <${TextField} label=${t('bookingPages.editor.name')} value=${draft.name} onInput=${(value) => change({ name: value })} isRequired=${true} maxLength=${BOOKING_LIMITS.nameLength} />
              <${SelectField} label=${t('bookingPages.editor.eventType')} value=${draft.eventType}
                onChange=${(value) => change({ eventType: /** @type {any} */ (value), durationMinutes: defaultDurationFor(value) })}
                options=${BOOKING_EVENT_TYPES.map((type) => ({ value: type, label: t(`calendar.eventTypes.${type}`) }))} />
            </div>
            <div class="form-field-row">
              <${NumberField} label=${t('bookingPages.editor.duration')} value=${draft.durationMinutes} min=${10} max=${480} onChange=${(value) => change({ durationMinutes: value })} />
              <${SelectField} label=${t('bookingPages.editor.timeZone')} value=${draft.timeZone} onChange=${(value) => change({ timeZone: value })}
                options=${(TIME_ZONES.includes(draft.timeZone) ? TIME_ZONES : [draft.timeZone, ...TIME_ZONES]).map((zone) => ({ value: zone, label: t(`bookingPages.timeZones.${zone.replace('/', '_')}`) || zone }))} />
            </div>
            <div class="booking-radio-group" role="radiogroup" aria-label=${t('bookingPages.editor.locationKind')}>
              <span class="form-field-label">${t('bookingPages.editor.locationKind')}</span>
              ${['IN_PERSON', 'GOOGLE_MEET'].map((kind) => html`<label key=${kind} class="booking-radio">
                <input type="radio" name="booking-location-kind" value=${kind} checked=${draft.locationKind === kind} onChange=${() => change({ locationKind: /** @type {any} */ (kind) })} />
                <span>${t(`bookingPages.editor.locationKinds.${kind}`)}</span>
              </label>`)}
            </div>
            ${draft.locationKind === 'IN_PERSON'
              ? html`<${TextField} label=${t('bookingPages.editor.location')} value=${draft.location ?? ''} onInput=${(value) => change({ location: value || null })}
                  maxLength=${BOOKING_LIMITS.locationLength} placeholder=${t('bookingPages.editor.locationPlaceholder')} />`
              : html`<p class="input-hint">${t('bookingPages.editor.meetHint')}</p>`}
          </div>
        <//>
        <${SettingsSection} title=${t('bookingPages.editor.availabilityTitle')} description=${t('bookingPages.editor.availabilityDescription')}>
          <div class="channel-form">
            <div class="booking-radio-group booking-source-options" role="radiogroup" aria-label=${t('bookingPages.editor.source')}>
              ${['GOOGLE_CALENDAR', 'WEEKLY_HOURS'].map((source) => html`<label key=${source} class="booking-source-option" data-selected=${draft.availabilitySource === source ? 'true' : 'false'}>
                <input type="radio" name="booking-source" value=${source} checked=${draft.availabilitySource === source} onChange=${() => change({ availabilitySource: /** @type {any} */ (source) })} />
                <span class="booking-source-title">${t(`bookingPages.editor.sources.${source}.label`)}</span>
                <span class="input-hint">${t(`bookingPages.editor.sources.${source}.description`)}</span>
              </label>`)}
            </div>
            <span class="form-field-label">${t('bookingPages.editor.hosts')}</span>
            <${BookingHostPicker} hosts=${hosts} value=${draft.hosts} availabilitySource=${draft.availabilitySource} disabled=${!canEdit}
              invalidMemberIds=${invalidMemberIds} currentMemberId=${currentMemberId} busyMemberId=${busyMemberId}
              onChange=${(value) => change({ hosts: value })} onResolve=${resolveHost} />
            <span class="input-hint">${t('bookingPages.hosts.pendingRule')}</span>
            ${draft.hosts.length > 1
              ? html`<${SelectField} label=${t('bookingPages.editor.hostAssignment')} value=${draft.hostAssignment} onChange=${(value) => change({ hostAssignment: /** @type {any} */ (value) })}
                  options=${['ROUND_ROBIN', 'FIRST_AVAILABLE'].map((value) => ({ value, label: t(`bookingPages.editor.assignments.${value}`) }))} />`
              : null}
            ${draft.availabilitySource === 'WEEKLY_HOURS'
              ? html`<span class="form-field-label">${t('bookingPages.editor.weeklyHours')}</span>
                  <${WeeklyHoursEditor} value=${draft.weeklyHours} disabled=${!canEdit} onChange=${(value) => change({ weeklyHours: value })} />`
              : null}
            <div class="form-field-row booking-number-row">
              <${NumberField} label=${t('bookingPages.editor.minimumNotice')} hint=${t('bookingPages.editor.minimumNoticeHint')} value=${Math.round(draft.minimumNoticeMinutes / 60)} min=${0} max=${720}
                onChange=${(value) => change({ minimumNoticeMinutes: value * 60 })} />
              <${NumberField} label=${t('bookingPages.editor.maximumDaysAhead')} value=${draft.maximumDaysAhead} min=${1} max=${60} onChange=${(value) => change({ maximumDaysAhead: value })} />
              <${NumberField} label=${t('bookingPages.editor.buffer')} hint=${t('bookingPages.editor.bufferHint')} value=${draft.bufferMinutes} min=${0} max=${240} onChange=${(value) => change({ bufferMinutes: value })} />
              <${NumberField} label=${t('bookingPages.editor.slotInterval')} hint=${t('bookingPages.editor.slotIntervalHint')} value=${draft.slotIntervalMinutes} min=${5} max=${240} onChange=${(value) => change({ slotIntervalMinutes: value })} />
            </div>
            <${TextField} label=${t('bookingPages.editor.blockedDates')} hint=${t('bookingPages.editor.blockedDatesHint')} multiline=${true} value=${blockedText} onInput=${setBlockedText}
              placeholder="02/11/2026" />
          </div>
        <//>
        <${SettingsSection} title=${t('bookingPages.editor.formTitle')} description=${t('bookingPages.editor.formDescription')}>
          <${QuestionsEditor} questions=${draft.questions} disabled=${!canEdit} onChange=${(questions) => change({ questions })} />
          <label class="integration-toggle-row">
            <${Toggle} value=${draft.offerMarketingOptIn} onChange=${(value) => change({ offerMarketingOptIn: value })} ariaLabel=${t('bookingPages.editor.marketing')} />
            <span>${t('bookingPages.editor.marketing')}<span class="input-hint integration-block-hint">${t('bookingPages.editor.marketingHint')}</span></span>
          </label>
        <//>
        <${SettingsSection} title=${t('bookingPages.editor.afterTitle')} description=${t('bookingPages.editor.afterDescription')}>
          <div class="channel-form">
            <${TextField} label=${t('bookingPages.editor.confirmationMessage')} value=${draft.confirmationMessage ?? ''} multiline=${true} maxLength=${BOOKING_LIMITS.confirmationLength}
              onInput=${(value) => change({ confirmationMessage: value || null })} placeholder=${t('bookingPages.editor.confirmationPlaceholder')} />
            <div class="form-field-row">
              <${SelectField} label=${t('bookingPages.editor.cardStage')} value=${draft.cardStage ?? ''} hint=${t('bookingPages.editor.cardStageHint')}
                onChange=${(value) => change({ cardStage: value || null })} options=${[{ value: '', label: t('bookingPages.editor.keepStage') }, ...stageOptions]} />
              <${SelectField} label=${t('bookingPages.editor.leadSource')} value=${draft.leadSource ?? ''}
                onChange=${(value) => change({ leadSource: value || null })} options=${[{ value: '', label: t('bookingPages.editor.noLeadSource') }, ...leadSourceOptions]} />
            </div>
            <${TextField} label=${t('bookingPages.editor.fixedGuests')} hint=${t('bookingPages.editor.fixedGuestsHint')} multiline=${true} value=${guestsText} onInput=${setGuestsText}
              placeholder="direcao@escola.com.br" />
          </div>
        <//>
      </fieldset>
      ${canEdit ? html`<div class="channel-form-actions booking-save-row">
        <${Button} title=${t('bookingPages.editor.save')} icon="IconCheck" accent="blue" size="small" isLoading=${isSaving} disabled=${!draft.name.trim()} onClick=${() => save()} />
      </div>` : null}
      <${SettingsSection} title=${t('bookingPages.preview.title')} description=${t('bookingPages.preview.description')}>
        <${BookingSlotPreview} bookingPageId=${page.id} timeZone=${page.timeZone} refreshKey=${page.updatedAt} />
      <//>
      <${SettingsSection} title=${t('bookingPages.bookings.title')} description=${t('bookingPages.bookings.description')}>
        <${BookingList} bookingPageId=${page.id} canEdit=${canEdit} refreshKey=${page.updatedAt} />
      <//>
      ${canEdit
        ? html`<${SettingsSection} title=${t('bookingPages.editor.dangerZone')}>
            <div><${Button} title=${t('bookingPages.editor.delete')} icon="IconTrash" variant="secondary" accent="danger" size="small" onClick=${() => setIsDeleting(true)} /></div>
          <//>`
        : null}
      ${isRegenerating ? html`<${Modal} title=${t('bookingPages.editor.regenerateTitle')} subtitle=${t('bookingPages.editor.regenerateSubtitle')}
        confirmLabel=${t('bookingPages.editor.regenerate')} confirmAccent="danger" isLoading=${isSaving} onClose=${() => setIsRegenerating(false)}
        onConfirm=${() => save({ regeneratePublicId: true })} />` : null}
      ${isDeleting ? html`<${Modal} title=${t('bookingPages.editor.deleteTitle')} subtitle=${t('bookingPages.editor.deleteSubtitle')} confirmLabel=${t('bookingPages.editor.delete')}
        confirmAccent="danger" onClose=${() => setIsDeleting(false)} onConfirm=${async () => {
          try {
            await deleteBookingPage(page.id);
            showSuccessSnackbar(t('bookingPages.editor.deleted'));
            navigate('/settings/booking-pages', { replace: true });
          } catch (requestError) {
            showErrorSnackbar(requestError);
            setIsDeleting(false);
          }
        }} />` : null}
    </div>
  <//>`;
}
